import { Button, Tag, Typography } from 'antd';
import { PermissionGuard } from '@/components/PermissionGuard';
import type { PromotionCartEvaluationDto } from '@/services/promotion.service';

const { Text } = Typography;

export type PromotionFreeItem = PromotionCartEvaluationDto['free_items'][number];

interface Props {
  freeItem: PromotionFreeItem;
  /** Đang resolve biến thể quà / gọi API kho — khoá nút để không thêm hai lần. */
  pending: boolean;
  onAdd: (freeItem: PromotionFreeItem) => void;
}

/**
 * Banner "đơn đủ điều kiện nhận quà" — đặt trên đầu giỏ hàng, cùng vị trí và
 * cùng kiểu với `ComboSuggestBanner` để thu ngân không phải học thêm một ngôn
 * ngữ hình ảnh mới.
 *
 * Cố ý KHÔNG tự thêm quà: xem spec §2 D4 (tránh vòng lặp evaluate và tránh âm
 * thầm trừ tồn kho).
 *
 * TCR-1074 (final review, Important 3) — GATE `pos.discount`. Dòng quà 0đ làm
 * đơn có `hasOverride = true` ⇒ checkout POS đi qua `PosDiscountGuard` và ném
 * 403 "Bạn không có quyền sửa giá/CK cho đơn hàng (pos.discount)". Với tenant
 * đã thu quyền đó của thu ngân, banner là một cái BẪY: bấm "Thêm quà" xong
 * checkout gãy với thông báo nói về "sửa giá", chẳng liên quan gì tới quà.
 * Sửa đúng gốc (đảo thứ tự cổng RBAC với evaluate, để việc đóng dấu quà chạy
 * TRƯỚC cổng) là thay đổi cấu trúc — park lại; ẩn banner là vài dòng và phải
 * làm ngay.
 *
 * Đặt gate Ở ĐÂY chứ không ở hai chỗ render (`POS/index.tsx` +
 * `Orders/edit.tsx`): cùng lý do đã gộp `handleAddPromotionGift` vào
 * `usePromotionGiftAdd` (Task 6/7 fix round 1, I4) — hai bản chép tay của
 * cùng một luật đã từng lệch nhau một lần rồi.
 */
export function PromotionGiftBanner({ freeItem, pending, onAdd }: Props) {
  return (
    <PermissionGuard permission="pos.discount">
      <div
        data-testid="pos-promo-gift"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#fff6f8',
          border: '1px solid #f0c4d0',
          borderLeft: '3px solid #a4193d',
          borderRadius: 5,
          padding: '8px 11px',
          margin: '0 12px 6px',
          flexShrink: 0,
        }}
      >
        <Tag color="#a4193d" style={{ margin: 0, fontSize: 10, fontWeight: 700 }}>
          QUÀ TẶNG
        </Tag>
        <Text style={{ fontSize: 13 }}>
          Đơn đủ điều kiện nhận{' '}
          <Text strong>
            {freeItem.quantity}× {freeItem.product_name ?? 'quà tặng'}
          </Text>
        </Text>
        <div style={{ marginLeft: 'auto' }}>
          <Button
            size="small"
            type="primary"
            disabled={pending}
            loading={pending}
            data-testid="pos-promo-gift-add-btn"
            style={{ background: '#a4193d', borderColor: '#a4193d' }}
            onClick={() => onAdd(freeItem)}
          >
            Thêm quà
          </Button>
        </div>
      </div>
    </PermissionGuard>
  );
}
